import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Button,
  // Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  // FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  // InputLabel,
  // InputLabel,
  //   IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { ReactComponent as DownRepo } from 'assets/images/ic_down-repo.svg';
import { ReactComponent as Draft } from 'assets/images/ic_draft.svg';
import {
  AppFormikButtonTiles,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { Formik } from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DateFnsAdapter from '@date-io/date-fns';
import { ReactComponent as Edit } from 'assets/images/ic_edit.svg';
import { ReactComponent as Trash } from 'assets/images/ic_trash.svg';
import { ReactComponent as Back } from 'assets/images/ic_back.svg';
// import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { PoliciesService } from 'services';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Utils } from 'shared/helpers';
import Acknowledgement from './components/Acknowledgement';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Approvals from './components/Approvals';
import ReviewPolicy from './components/ReviewPolicy';
import ReviewHistory from './components/ReviewHistory';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import Compliance from './components/Compliance';
import useBroadCast from 'shared/hooks/useBroadCast';
import HealthCheck from './components/HealthChek';
import PolicyVersions from './components/Versions';
import { IPolicy } from '../Main';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

const dateFns = new DateFnsAdapter();

const PolicyDetail = (): ReactElement => {
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const { policyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [policyDetail, setPolicyDetail] = useState<IPolicy>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPolicyReview, setShowPolicyReview] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  // API Calls
  const loadDetails = (): void => {
    setIsLoadingDetail(!policyDetail);
    PoliciesService.detail(`${policyId}`)
      .then((response) => {
        const { data } = response.data;
        setPolicyDetail(data);
      })
      .catch(() => {
        navigate('/policy-management');
      })
      .finally(() => {
        setIsLoadingDetail(false);
      });
  };

  // UI Actions
  const actionOpenStatus = (event: any): void => {
    if (policyDetail?.status !== 'Archived') {
      setAnchorEl(event.currentTarget);
    }
  };
  const actionCloseStatus = (): void => {
    setAnchorEl(null);
  };
  const actionOnChangeStatus = (status: string): void => {
    actionCloseStatus();
    PoliciesService.update(`${policyId}`, { ...policyDetail, status })
      .then(() => {
        loadDetails();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };
  const actionShowDeleteDialog = (): void => {
    setShowDeleteDialog(true);
  };
  const actionDeletePolicy = (): void => {
    setIsDeleting(true);
    PoliciesService.deleteRecord(`${policyId}`)
      .then(() => {
        navigate(-1);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        setIsDeleting(false);
      });
  };

  // Hooks
  useEffect(() => {
    if (policyId) loadDetails();
  }, [policyId]);

  useBroadCast({
    eventName: BROADCAST_MESSAGES.RELOAD_POLICY_DETAILS,
    onBroadCast: () => {
      loadDetails();
    },
  });

  const getInitialTab = (): boolean[] => {
    const tab = location.state?.activeTab;

    switch (tab) {
      case 'acknowledgements':
        return [false, false, false, false, true, false, false];
      case 'approval':
        return [false, false, false, true, false, false, false];
      default:
        return [true, false, false, false, false, false, false];
    }
  };

  // UI Elements
  return (
    <Box className="mx-8 my-5">
      <HeadTag title="Policy Detail" />
      {isLoadingDetail && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetail && policyDetail && (
        <Box className=" mt-4">
          <Stack direction="row" justifyContent="space-between">
            <AppPrimaryButton
              title="Back to Policies"
              startIcon={
                <SvgIcon
                  component={Back}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                  htmlColor="white"
                />
              }
              onClick={() => {
                navigate(-1);
              }}
            />
            <Stack direction="row" spacing={2}>
              {orgACLConfig.policies?.includes(
                'policies.policyManagement.view.editPolicy'
              ) && (
                <AppPrimaryButton
                  title="Edit Policy"
                  startIcon={
                    <SvgIcon
                      component={Edit}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="white"
                    />
                  }
                  onClick={() => {
                    navigate(`/policy-management/edit/${policyId}`);
                  }}
                />
              )}
              {orgACLConfig.policies?.includes(
                'policies.policyManagement.view.deletePolicy'
              ) && (
                <AppPrimaryButton
                  title="Delete Policy"
                  className="btn-danger"
                  isLoading={isDeleting}
                  disableRipple={isDeleting}
                  startIcon={
                    <SvgIcon
                      component={Trash}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="white"
                    />
                  }
                  onClick={actionShowDeleteDialog}
                />
              )}
              {orgACLConfig.policies?.includes(
                'policies.policyManagement.view.markAsReviewed'
              ) && (
                <AppPrimaryButton
                  title="Mark as Reviewed"
                  startIcon={<TaskAltIcon />}
                  onClick={() => {
                    setShowPolicyReview(true);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Formik
            initialValues={{ status: getInitialTab() }}
            onSubmit={() => console.log('test')}
          >
            {({ values }) => (
              <Stack direction="column" spacing={2}>
                <Stack
                  style={{
                    backgroundColor: 'white',
                    padding: '10px 20px',
                    margin: '20px 0px',
                  }}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Box className="">
                    <Stack direction="row" spacing={2}>
                      <Typography variant="h3">{policyDetail.title}</Typography>
                      <Chip size="small" label={policyDetail.status} />
                    </Stack>
                    <Typography variant="body1">
                      Version {policyDetail.version} | {policyDetail.owner}
                    </Typography>
                  </Box>

                  <Stack>
                    <Typography variant="h3">Publication Status</Typography>
                    <Typography variant="body1">
                      Current approval state of this policy
                    </Typography>
                  </Stack>
                  <Stack
                    justifyContent="center"
                    className="cursor-pointer"
                    onClick={actionOpenStatus}
                  >
                    <Stack spacing={2} direction="row" alignItems="center">
                      <SvgIcon
                        component={Draft}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#3D3C42"
                      />
                      <Typography variant="h3">
                        {policyDetail.status}
                      </Typography>
                    </Stack>
                    {policyDetail.status !== 'Archived' && (
                      <Stack spacing={2} direction="row" alignItems="center">
                        <Typography variant="body1">
                          Change publication status
                        </Typography>
                        <SvgIcon
                          component={DownRepo}
                          inheritViewBox
                          className="view-icon"
                          fontSize="inherit"
                          htmlColor="#3D3C42"
                        />
                      </Stack>
                    )}
                  </Stack>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={actionCloseStatus}
                  >
                    {['Draft', 'Review', 'Published', 'Archived'].map(
                      (status) => (
                        <MenuItem
                          key={status}
                          onClick={() => actionOnChangeStatus(status)}
                        >
                          {status}
                        </MenuItem>
                      )
                    )}
                  </Menu>
                </Stack>
                <AppFormikButtonTiles
                  variant="outlined"
                  name="status"
                  size="small"
                  label=""
                  disabled={false}
                  multichoice={false}
                  className="app-group-button"
                  buttons={[
                    {
                      value: 'Details',
                      tooltip:
                        'View and edit policy core information, scope, owner assignments, enforcement status, and description.',
                    },
                    {
                      value: 'Compliance',
                      tooltip:
                        'Map policy controls to external regulatory frameworks, standards, and internal compliance requirements.',
                    },
                    {
                      value: 'Versions',
                      tooltip:
                        'Track major and minor policy updates, draft revisions, publication dates, and historical changes.',
                    },
                    {
                      value: 'Approvals',
                      tooltip:
                        'Review sign-off workflows, pending stakeholder approvals, and governance authorization records.',
                    },
                    {
                      value: 'Acknowledgements',
                      tooltip:
                        'Track user sign-offs, attestation records, and employee agreement logs for this policy.',
                    },
                    {
                      value: 'Review History',
                      tooltip:
                        'Audit scheduled policy evaluations, past reviewer notes, and periodic compliance assessments.',
                    },
                    {
                      value: 'Health Check',
                      tooltip:
                        'Monitor automated policy effectiveness, enforcement readiness, and operational alignment metrics.',
                    },
                  ]}
                  outerLabel
                />
                {values.status[0] && (
                  <Box>
                    <Box className="app-plain-card">
                      <Box className="title">
                        <Typography variant="h4">Basic Information</Typography>
                      </Box>
                      <Box className="content">
                        <Grid spacing={5} container>
                          <Grid xs={6} item>
                            <Box className="properties">
                              <Typography className="property-value mb-2">
                                Status
                              </Typography>
                              <Chip
                                color="primary"
                                label={policyDetail.status}
                              />
                            </Box>
                          </Grid>
                          <Grid xs={6} item>
                            <Box className="properties">
                              <Typography className="property-value mb-2">
                                Version
                              </Typography>
                              <Typography className="property">
                                {policyDetail.version}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid xs={6} item>
                            <Box className="properties">
                              <Typography className="property-value mb-2">
                                Owner
                              </Typography>
                              <Typography className="property">
                                {policyDetail.owner}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid xs={6} item>
                            <Box className="properties">
                              <Typography className="property-value mb-2">
                                Mode
                              </Typography>
                              <Typography className="property">
                                {policyDetail.policyMode}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid xs={12} item>
                            <Box className="properties">
                              <Typography className="property-value mb-2">
                                Description
                              </Typography>
                              <Typography className="property">
                                {policyDetail.description}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                    <Box className="app-plain-card">
                      <Box className="title">
                        <Typography variant="h4">Important Dates</Typography>
                      </Box>
                      <Box className="content">
                        <Grid spacing={5} container>
                          {policyDetail.effectiveDate && (
                            <Grid xs={6} item>
                              <Box className="properties">
                                <Typography className="property-value mb-2">
                                  Effective Date
                                </Typography>
                                <Typography>
                                  {dateFns
                                    .date(policyDetail.effectiveDate)
                                    .toDateString()}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {policyDetail.publishedDate && (
                            <Grid xs={6} item>
                              <Box className="properties">
                                <Typography className="property-value mb-2">
                                  Published Date
                                </Typography>
                                <Typography>
                                  {dateFns
                                    .date(policyDetail.publishedDate)
                                    .toDateString()}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {policyDetail.approvedDate && (
                            <Grid xs={6} item>
                              <Box className="properties">
                                <Typography className="property-value mb-2">
                                  Approved Date
                                </Typography>
                                <Typography>
                                  {dateFns
                                    .date(policyDetail.approvedDate)
                                    .toDateString()}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {policyDetail.nextReviewDate && (
                            <Grid xs={6} item>
                              <Box className="properties">
                                <Typography className="property-value mb-2">
                                  Next Review Date
                                </Typography>
                                <Typography>
                                  {dateFns
                                    .date(policyDetail.nextReviewDate)
                                    .toDateString()}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Box>
                    <Box className="app-plain-card">
                      <Box className="title">
                        <Typography variant="h4">Policy Content</Typography>
                      </Box>

                      <Box className="content">
                        {policyDetail.policyMode === 'Url' && (
                          <Button
                            variant="text"
                            onClick={() => {
                              window.open(
                                policyDetail.policyUrl,
                                'policy-content'
                              );
                            }}
                          >
                            {policyDetail.policyUrl}
                          </Button>
                          // <Typography className='word-break'>{policyDetail.policyUrl}</Typography>
                        )}
                        {policyDetail.policyMode === 'Content' && (
                          <div
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              overflowX: 'auto',
                              maxWidth: '100%',
                              fontFamily: 'sans-serif',
                            }}
                            dangerouslySetInnerHTML={{
                              __html: policyDetail.content,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box className="app-plain-card">
                      <Box className="title">
                        <Typography variant="h4">Settings</Typography>
                      </Box>
                      <Box className="content">
                        <Grid spacing={5} container>
                          <Grid xs={6} item>
                            <Box className="properties">
                              <Typography className="property-value mb-2">
                                Acknowledgement Required
                              </Typography>
                              <Typography className="property">
                                {policyDetail.acknowledgementRequired
                                  ? 'Yes'
                                  : 'No'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid xs={6} item>
                            <Box className="properties">
                              <Typography className="property-value mb-2">
                                Version
                              </Typography>
                              <Typography className="property">
                                {policyDetail.version}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Box>
                )}
                {values.status[1] && (
                  <Compliance addedFrameworks={policyDetail.frameworkIds} />
                )}
                {values.status[2] && <>{true && <PolicyVersions />}</>}
                {values.status[3] && <Approvals policyId={`${policyId}`} />}
                {values.status[4] && (
                  <Acknowledgement policyId={`${policyId}`} />
                )}
                {values.status[5] && <ReviewHistory policyId={`${policyId}`} />}
                {values.status[6] && <HealthCheck />}
              </Stack>
            )}
          </Formik>
        </Box>
      )}

      <Dialog maxWidth="xs" fullWidth open={showDeleteDialog}>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h3">Delete Policy</Typography>
            <IconButton onClick={() => setShowDeleteDialog(false)}>
              <HighlightOffIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography className="mb-2">
            Reason for Deletion (Optional)
          </Typography>
          <TextField
            fullWidth
            // label="Reason for Deletion (Optional)"
            placeholder="Enter a reason for deleting this policy..."
            multiline
            rows={4}
          />
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#f7f9fe',
              border: '1px solid lightgray',
              borderRadius: 2,
            }}
          >
            <Typography>
              <b>Note:</b> This policy will be soft deleted and permanently
              removed after 90 days. Completed acknowledgements will be kept
              indefinitely for compliance purposes.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <AppPrimaryButton
            isLoading={isDeleting}
            disableRipple={isDeleting}
            className="mr-5 mb-2"
            title="Delete Policy"
            onClick={actionDeletePolicy}
          />
        </DialogActions>
      </Dialog>

      {policyDetail && (
        <ReviewPolicy
          open={showPolicyReview}
          onClose={(reload) => {
            setShowPolicyReview(false);
            if (reload) {
              window.postMessage(BROADCAST_MESSAGES.RELOAD_ASSESSMENTS);
            }
          }}
          policy={policyDetail}
        />
      )}
    </Box>
  );
};

export default PolicyDetail;
